import { Navigate, useLocation } from "react-router-dom";
import { useGetProfileQuery } from "@/store";
import type { ReactNode } from "react";

interface AdminProtectedRouteProps {
  children: ReactNode;
}

/**
 * Protected route specifically for admin pages
 * Requires authentication and admin/staff role
 */
export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const location = useLocation();

  // Fetch user profile to verify authentication (cookie-based)
  const { data, isLoading, error } = useGetProfileQuery();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // If error or no user data, authentication failed
  if (error || !data?.data) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  const user = data.data;

  // Check if user has admin or staff role (backend uses uppercase: ADMIN, STAFF, SUPER_ADMIN)
  const userRole = user.role?.toUpperCase();
  if (userRole !== "ADMIN" && userRole !== "STAFF" && userRole !== "SUPER_ADMIN") {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
